import { createContext, useContext } from 'react'
import type { PropsWithChildren } from 'react'

import { useAuthentication } from '@motrix/authentication'

import { contentGenerationPermissions } from '../content-generation'
import { contentPlanningPermissions } from '../content-planning'
import { curriculaPermissions } from '../curricula'
import { frameworkPermissions } from '../framework'
import { reportsPermissions } from '../reports'
import { taggingPermissions } from '../tagging'
import type {
  PlatformsPermissions,
  PlatformsPermissionsAsString,
} from '../types'
import { focusPermissions } from '../focus'

const PermissionsContext = createContext({} as PlatformsPermissions)

export const PermissionsProvider = ({ children }: PropsWithChildren) => {
  const { me } = useAuthentication()

  if (!me?.currentWorkspace) return null

  const allowedActions = new Set<PlatformsPermissionsAsString>(
    me.currentWorkspace.actions,
  )

  const permissions: PlatformsPermissions = {
    contentGeneration: contentGenerationPermissions(allowedActions),
    contentPlanning: contentPlanningPermissions(allowedActions),
    curricula: curriculaPermissions(allowedActions),
    framework: frameworkPermissions(allowedActions),
    focus: focusPermissions(allowedActions),
    reports: reportsPermissions(allowedActions),
    tagging: taggingPermissions(allowedActions),
  }

  return (
    <PermissionsContext.Provider value={permissions}>
      {children}
    </PermissionsContext.Provider>
  )
}

export const usePermissions = () => {
  const permissions = useContext(PermissionsContext)

  if (!permissions)
    throw new Error('usePermissions must be used inside a PermissionsProvider.')

  return permissions
}
